import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText, Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * "My documents" panel on the Account page.
 *
 * Lists every Risk Disclosure & Enrollment Agreement the user has
 * signed and lets them re-download the signed PDF at any time. The
 * PDF is regenerated server-side from the immutable
 * `agreement_signatures` record — we don't store the file itself.
 */
export default function SignedDocuments() {
  const { t } = useTranslation();
  const [docs, setDocs] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/agreement/documents");
        if (mounted) setDocs(data.documents || []);
      } catch {
        if (mounted) setDocs([]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const download = async (tier, version) => {
    setDownloading(`${tier}-${version}`);
    try {
      const res = await api.get(`/agreement/pdf?tier=${tier}&version=${version}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `peak-market-society-agreement-${tier}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setDownloading(null);
    }
  };

  // Hide the whole panel until the fetch completes, so we don't flash
  // an empty state on mount.
  if (docs === null) return null;
  if (docs.length === 0) return null;

  return (
    <div
      data-testid="account-signed-documents"
      className="bg-white border border-slate-200 rounded-2xl p-8"
    >
      <div className="flex items-start gap-3 mb-1">
        <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0 mt-0.5">
          <FileText size={16} className="text-blue-800" />
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">
            {t("signedDocs.title")}
          </div>
          <p className="mt-1 text-sm text-slate-600 leading-relaxed">
            {t("signedDocs.body")}
          </p>
        </div>
      </div>

      <div className="mt-6 divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
        {docs.map((d) => {
          const id = `${d.tier}-${d.version}`;
          const busy = downloading === id;
          const signedDate = new Date(d.signed_at).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "numeric",
            hour: "2-digit", minute: "2-digit",
          });
          return (
            <div
              key={id}
              data-testid={`signed-doc-${d.tier}`}
              className="flex items-center gap-4 px-4 py-3 bg-slate-50/40 hover:bg-white transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm">
                  {d.tier === "premium" ? t("signedDocs.premiumAgreement") : t("signedDocs.basicAgreement")}
                </div>
                <div className="mt-0.5 text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>{t("signedDocs.signedByPrefix")} <span className="font-medium text-slate-700">{d.full_name}</span></span>
                  <span>·</span>
                  <span>{signedDate}</span>
                  <span>·</span>
                  <span className="font-mono">v{d.version}</span>
                </div>
              </div>
              <button
                type="button"
                data-testid={`signed-doc-download-${d.tier}`}
                onClick={() => download(d.tier, d.version)}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-800 hover:bg-blue-700 text-white text-xs font-semibold shadow-sm disabled:opacity-60 transition-colors"
              >
                <Download size={13} />
                {busy ? t("signedDocs.preparing") : t("signedDocs.downloadPdf")}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
